import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
    getProposalById,
    updateProposal,
    markAsReadyForReview,
    submitMockProposal,
    getProposalStatus,
    getMockClientFeedback,
    deleteProposal
} from '../services/proposalService';
import { usePermissions } from '../hooks/usePermissions';
import { ArrowLeft, Home, Settings, Edit, Eye, Plus, X, Upload, ExternalLink, MapPin, Clock, CheckCircle } from 'lucide-react';
import { FileUpload } from './FileUpload';
import { FileViewerModal } from './FileViewerModal';

export function ProposalDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { canAccessAdminPanel } = usePermissions();
    const [proposal, setProposal] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [actionLoading, setActionLoading] = useState(false);
    const [submissionStatus, setSubmissionStatus] = useState(null);
    const [clientFeedback, setClientFeedback] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editData, setEditData] = useState({});
    const [showFileViewer, setShowFileViewer] = useState(false);
    const [selectedAttachment, setSelectedAttachment] = useState(null);
    const [selectedAttachmentIndex, setSelectedAttachmentIndex] = useState(null);

    useEffect(() => {
        if (id) {
            loadProposal();
        }
    }, [id]);

    const loadProposal = async () => {
        try {
            setLoading(true);
            const response = await getProposalById(id);

            if (response.success) {
                setProposal(response.data);

                // Only load mock submission status for app-created proposals (not Upwork-synced)
                if (response.data.status === 'submitted' && response.data.source !== 'upwork') {
                    loadSubmissionStatus();
                    loadClientFeedback();
                }
            } else {
                setError(response.error || 'Failed to load proposal');
            }
        } catch (error) {
            setError(error.message || 'Failed to load proposal');
        } finally {
            setLoading(false);
        }
    };

    const loadSubmissionStatus = async () => {
        try {
            const response = await getProposalStatus(id);
            if (response.success) {
                setSubmissionStatus(response.data);
            }
        } catch {
            // Silent — non-critical supplemental data
        }
    };

    const loadClientFeedback = async () => {
        try {
            const response = await getMockClientFeedback(id);
            if (response.success) {
                setClientFeedback(response.data);
            }
        } catch {
            // Silent — non-critical supplemental data
        }
    };

    const handleMarkForReview = async () => {
        try {
            setActionLoading(true);
            const response = await markAsReadyForReview(id);
            if (response.success) {
                loadProposal();
            } else {
                setError(response.error || 'Failed to mark proposal for review');
            }
        } catch (error) {
            setError(error.message || 'Failed to mark proposal for review');
        } finally {
            setActionLoading(false);
        }
    };

    const handleSubmit = async () => {
        if (!window.confirm('Are you sure you want to submit this proposal to Upwork?')) {
            return;
        }

        try {
            setActionLoading(true);
            const response = await submitMockProposal(id);
            if (response.success) {
                loadProposal();
                loadSubmissionStatus();
            } else {
                setError(response.error || 'Failed to submit proposal');
            }
        } catch (error) {
            setError(error.message || 'Failed to submit proposal');
        } finally {
            setActionLoading(false);
        }
    };

    const handleEdit = () => {
        setEditData({
            title: proposal.title,
            description: proposal.description,
            coverLetter: proposal.coverLetter,
            pricing: proposal.pricing,
            attachments: proposal.attachments || [],
            portfolioItems: proposal.portfolioItems || [],
            links: proposal.links || [],
            screeningAnswers: proposal.screeningAnswers || []
        });
        setIsEditing(true);
    };

    const handleSaveEdit = async () => {
        try {
            setActionLoading(true);
            const response = await updateProposal(id, editData);

            if (response.success) {
                setProposal(response.data);
                setIsEditing(false);
                setEditData({});
            } else {
                setError(response.error || 'Failed to update proposal');
            }
        } catch (error) {
            setError(error.message || 'Failed to update proposal');
        } finally {
            setActionLoading(false);
        }
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setEditData({});
    };

    const handleEditChange = (field, value) => {
        setEditData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleDelete = async () => {
        if (!window.confirm('Are you sure you want to delete this proposal? This action cannot be undone.')) {
            return;
        }

        try {
            setActionLoading(true);
            const response = await deleteProposal(id);

            if (response.success) {
                navigate('/proposals');
            } else {
                setError(response.error || 'Failed to delete proposal');
            }
        } catch (error) {
            setError('Failed to delete proposal: ' + error.message);
        } finally {
            setActionLoading(false);
        }
    };

    const handleViewAttachment = (attachment, index) => {
        setSelectedAttachment(attachment);
        setSelectedAttachmentIndex(index);
        setShowFileViewer(true);
    };

    const closeFileViewer = () => {
        setShowFileViewer(false);
        setSelectedAttachment(null);
        setSelectedAttachmentIndex(null);
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            draft: { color: 'bg-gray-100 text-gray-800', label: 'Draft' },
            ready_for_review: { color: 'bg-yellow-100 text-yellow-800', label: 'Ready for Review' },
            under_review: { color: 'bg-primary-100 text-primary-800', label: 'Under Review' },
            approved: { color: 'bg-success-100 text-success-800', label: 'Approved' },
            rejected: { color: 'bg-red-100 text-red-800', label: 'Rejected' },
            submitted: { color: 'bg-purple-100 text-purple-800', label: 'Submitted' },
            submission_failed: { color: 'bg-red-100 text-red-800', label: 'Submission Failed' }
        };

        const config = statusConfig[status] || statusConfig.draft;
        return (
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
                {config.label}
            </span>
        );
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="max-w-6xl mx-auto p-6">
                <div className="flex items-center justify-center h-64">
                    <div className="text-gray-500">Loading proposal...</div>
                </div>
            </div>
        );
    }

    if (error || !proposal) {
        return (
            <div className="max-w-6xl mx-auto p-6">
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                    {error || 'Proposal not found'}
                </div>
                <div className="flex gap-2 mt-4">
                    <button
                        onClick={() => navigate('/proposals')}
                        className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Back to Proposals
                    </button>
                    <Link
                        to="/jobs"
                        className="flex items-center gap-2 bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors"
                    >
                        <Home className="h-4 w-4" />
                        Jobs
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50">
            {/* Header */}
            <div className="bg-white shadow-sm border-b">
                <div className="w-full px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => navigate('/proposals')}
                                className="flex items-center gap-4 text-gray-600 hover:text-gray-900 transition-colors"
                            >
                                <ArrowLeft className="h-4 w-4" />
                                Back to Proposals
                            </button>
                        </div>
                        <div className="flex items-center gap-3">
                            {proposal.source === 'upwork' && (
                                <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
                                    Synced from Upwork
                                </span>
                            )}
                            {proposal.source !== 'upwork' && (
                                <>
                                    {proposal.status === 'draft' && (
                                        <button
                                            onClick={handleEdit}
                                            className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors"
                                        >
                                            <Edit className="h-4 w-4" />
                                            Edit Proposal
                                        </button>
                                    )}

                                    {proposal.status === 'draft' && (
                                        <button
                                            onClick={() => handleMarkForReview(proposal._id)}
                                            disabled={actionLoading}
                                            className="flex items-center gap-2 bg-accent-600 text-white px-4 py-2 rounded-md hover:bg-accent-700 transition-colors disabled:opacity-50"
                                        >
                                            <Eye className="h-4 w-4" />
                                            {actionLoading ? 'Marking...' : 'Mark for Review'}
                                        </button>
                                    )}

                                    {proposal.status === 'approved' && (
                                        <>
                                            <button
                                                onClick={handleEdit}
                                                className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors"
                                            >
                                                <Edit className="h-4 w-4" />
                                                Edit Proposal
                                            </button>
                                            <button
                                                onClick={() => handleSubmit(proposal._id)}
                                                disabled={actionLoading}
                                                className="flex items-center gap-2 bg-success-600 text-white px-4 py-2 rounded-md hover:bg-success-700 transition-colors disabled:opacity-50"
                                            >
                                                <Upload className="h-4 w-4" />
                                                {actionLoading ? 'Submitting...' : 'Submit to Upwork'}
                                            </button>
                                        </>
                                    )}

                                    {proposal.status === 'rejected' && (
                                        <button
                                            onClick={handleEdit}
                                            className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors"
                                        >
                                            <Edit className="h-4 w-4" />
                                            Edit & Resubmit
                                        </button>
                                    )}

                                    {(proposal.status === 'draft' || proposal.status === 'rejected' || proposal.status === 'changes_requested' || proposal.status === 'approved') && (
                                        <button
                                            onClick={handleDelete}
                                            disabled={actionLoading}
                                            className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors disabled:opacity-50"
                                        >
                                            <X className="h-4 w-4" />
                                            {actionLoading ? 'Deleting...' : 'Delete'}
                                        </button>
                                    )}
                                </>
                            )}

                            {canAccessAdminPanel() && (
                                <Link
                                    to="/admin"
                                    className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors px-3 py-2 rounded-md hover:bg-gray-100"
                                >
                                    <Settings className="h-4 w-4" />
                                    Admin Panel
                                </Link>
                            )}
                            <Link
                                to="/jobs"
                                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors px-3 py-2 rounded-md hover:bg-gray-100"
                            >
                                <Home className="h-4 w-4" />
                                Jobs
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-6xl mx-auto p-6">
                <div className="bg-white rounded-lg shadow-md p-6">

                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">{proposal.title}</h1>
                        <div className="flex items-center gap-3 mt-1">
                            {getStatusBadge(proposal.status)}
                            {proposal.source && (
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    proposal.source === 'app' 
                                        ? 'bg-primary-100 text-primary-800' 
                                        : 'bg-purple-100 text-purple-800'
                                }`}>
                                    {proposal.source === 'app' ? 'App' : 'Upwork'}
                                </span>
                            )}
                            <span className="text-sm text-gray-500">
                                Created {formatDate(proposal.createdAt)}
                            </span>
                            {proposal.upworkSyncedAt && (
                                <span className="text-sm text-gray-500">
                                    • Synced {formatDate(proposal.upworkSyncedAt)}
                                </span>
                            )}
                            {proposal.metadata?.modifiedDateTime && (
                                <span className="text-sm text-gray-500">
                                    • Modified {formatDate(proposal.metadata.modifiedDateTime)}
                                </span>
                            )}
                        </div>
                        {proposal.source === 'upwork' && proposal.upworkStatus && (
                            <div className="mt-2 text-sm text-gray-600">
                                Upwork Status: <span className="font-medium">{proposal.upworkStatus}</span>
                            </div>
                        )}
                    </div>
                    {error && (
                        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                            {error}
                        </div>
                    )}

                    {/* Edit Form */}
                    {isEditing && proposal.source !== 'upwork' && (
                        <div className="bg-primary-50 border border-primary-200 rounded-lg p-6 mb-6">
                            <h3 className="text-lg font-semibold text-primary-900 mb-4">Edit Proposal</h3>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                                    <input
                                        type="text"
                                        value={editData.title || ''}
                                        onChange={(e) => handleEditChange('title', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                    <textarea
                                        value={editData.description || ''}
                                        onChange={(e) => handleEditChange('description', e.target.value)}
                                        rows={4}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Cover Letter</label>
                                    <textarea
                                        value={editData.coverLetter || ''}
                                        onChange={(e) => handleEditChange('coverLetter', e.target.value)}
                                        rows={6}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Pricing Type</label>
                                        <select
                                            value={editData.pricing?.type || ''}
                                            onChange={(e) => handleEditChange('pricing', { ...editData.pricing, type: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                        >
                                            <option value="fixed">Fixed Price</option>
                                            <option value="hourly">Hourly</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                                        <input
                                            type="number"
                                            value={editData.pricing?.amount || ''}
                                            onChange={(e) => handleEditChange('pricing', { ...editData.pricing, amount: parseFloat(e.target.value) })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                        />
                                    </div>
                                </div>

                                {/* Attachments */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Attachments</label>
                                    <FileUpload
                                        onFilesChange={(files) => {
                                            const formattedFiles = files.map(file => {
                                                // FileUpload returns base64 files, so create data URL
                                                const url = file.data
                                                    ? `data:${file.mimeType || 'application/octet-stream'};base64,${file.data}`
                                                    : file.url || '';

                                                return {
                                                    name: file.name,
                                                    url: url,
                                                    data: file.data, // Keep base64 data for upload
                                                    mimeType: file.mimeType || file.type,
                                                    type: (file.mimeType || file.type || '').startsWith('image/') ? 'image' :
                                                        (file.mimeType || file.type || '').startsWith('video/') ? 'video' :
                                                            (file.mimeType || file.type || '').includes('pdf') || (file.mimeType || file.type || '').includes('document') ? 'document' : 'other',
                                                    size: file.size
                                                };
                                            });
                                            handleEditChange('attachments', formattedFiles);
                                        }}
                                        maxFiles={5}
                                        acceptedTypes={['image/*', 'video/*', 'application/pdf', '.doc', '.docx']}
                                    />
                                    {editData.attachments && editData.attachments.length > 0 && (
                                        <div className="mt-2 space-y-2">
                                            {editData.attachments.map((attachment, index) => (
                                                <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                                    <span className="text-sm">{attachment.name}</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const newAttachments = editData.attachments.filter((_, i) => i !== index);
                                                            handleEditChange('attachments', newAttachments);
                                                        }}
                                                        className="text-red-600 hover:text-red-800"
                                                    >
                                                        <X className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Links */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Links</label>
                                    <div className="space-y-2">
                                        {editData.links && editData.links.map((link, index) => (
                                            <div key={index} className="flex gap-2">
                                                <input
                                                    type="text"
                                                    placeholder="Link title"
                                                    value={link.title || ''}
                                                    onChange={(e) => {
                                                        const newLinks = [...editData.links];
                                                        newLinks[index] = { ...link, title: e.target.value };
                                                        handleEditChange('links', newLinks);
                                                    }}
                                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                                />
                                                <input
                                                    type="url"
                                                    placeholder="URL"
                                                    value={link.url || ''}
                                                    onChange={(e) => {
                                                        const newLinks = [...editData.links];
                                                        newLinks[index] = { ...link, url: e.target.value };
                                                        handleEditChange('links', newLinks);
                                                    }}
                                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const newLinks = editData.links.filter((_, i) => i !== index);
                                                        handleEditChange('links', newLinks);
                                                    }}
                                                    className="text-red-600 hover:text-red-800 p-2"
                                                >
                                                    <X className="h-4 w-4" />
                                                </button>
                                            </div>
                                        ))}
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const newLinks = [...(editData.links || []), { title: '', url: '' }];
                                                handleEditChange('links', newLinks);
                                            }}
                                            className="flex items-center gap-2 text-primary-600 hover:text-primary-700"
                                        >
                                            <Plus className="h-4 w-4" />
                                            Add Link
                                        </button>
                                    </div>
                                </div>

                                {/* Portfolio Items */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Portfolio Items</label>
                                    <div className="space-y-3">
                                        {editData.portfolioItems && editData.portfolioItems.map((item, index) => (
                                            <div key={index} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                                                <div className="flex items-start justify-between">
                                                    <div className="flex-1">
                                                        <div className="font-medium text-gray-900 mb-1">{item.title}</div>
                                                        {item.category && (
                                                            <div className="text-sm text-gray-500 mb-1">{item.category}</div>
                                                        )}
                                                        {item.description && (
                                                            <p className="text-sm text-gray-700 mb-2">{item.description}</p>
                                                        )}
                                                        {item.url && (
                                                            <a
                                                                href={item.url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-primary-600 hover:text-primary-700 text-sm flex items-center gap-1"
                                                            >
                                                                <ExternalLink className="h-3 w-3" />
                                                                View Project
                                                            </a>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-2 ml-4">
                                                        {item.imageUrl && (
                                                            <img
                                                                src={item.imageUrl}
                                                                alt={item.title}
                                                                className="h-12 w-12 rounded-lg object-cover"
                                                            />
                                                        )}
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                const newItems = editData.portfolioItems.filter((_, i) => i !== index);
                                                                handleEditChange('portfolioItems', newItems);
                                                            }}
                                                            className="text-red-600 hover:text-red-800 p-1"
                                                            title="Remove portfolio item"
                                                        >
                                                            <X className="h-4 w-4" />
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="mt-2 text-xs text-gray-500">
                                                    Note: Portfolio items cannot be edited. You can only remove them or add new ones.
                                                </div>
                                            </div>
                                        ))}
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const newItems = [...(editData.portfolioItems || []), { title: '', description: '', url: '' }];
                                                handleEditChange('portfolioItems', newItems);
                                            }}
                                            className="flex items-center gap-2 text-primary-600 hover:text-primary-700 border border-dashed border-gray-300 rounded-lg p-3 hover:border-primary-400"
                                        >
                                            <Plus className="h-4 w-4" />
                                            Add New Portfolio Item
                                        </button>
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={handleSaveEdit}
                                        disabled={actionLoading}
                                        className="bg-success-600 text-white px-4 py-2 rounded-md hover:bg-success-700 disabled:opacity-50"
                                    >
                                        {actionLoading ? 'Saving...' : 'Save Changes'}
                                    </button>
                                    <button
                                        onClick={handleCancelEdit}
                                        className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Two Column Layout */}
                    <div className="flex gap-6">
                        {/* Main Content */}
                        <div className="flex-1 min-w-0">
                            {/* Job Details - Upwork Style */}
                            <div className="border border-gray-200 rounded-lg p-5 mb-6">
                                <h3 className="font-semibold text-gray-900 text-lg mb-1">Job details</h3>
                                <div className="flex gap-6">
                                    {/* Left sub-column: title, category, description, link */}
                                    <div className="flex-1 min-w-0">
                                        {/* Job Title */}
                                        <h4 className="text-base font-medium text-gray-900 mt-4">
                                            {proposal.jobPostingId?.title || proposal.metadata?.jobTitle || 'N/A'}
                                        </h4>

                                        {/* Category & Posted Date */}
                                        <div className="flex items-center gap-3 mt-2 text-sm">
                                            {proposal.metadata?.category && (
                                                <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                                                    {proposal.metadata.category}
                                                </span>
                                            )}
                                            {proposal.proposalSentAt && (
                                                <span className="text-gray-500">
                                                    Initiated {formatDate(proposal.proposalSentAt)}
                                                </span>
                                            )}
                                        </div>

                                        {/* Job Description */}
                                        <div className="mt-4">
                                            <p className="text-gray-700 text-sm whitespace-pre-wrap">
                                                {proposal.metadata?.jobDescription || proposal.description}
                                            </p>
                                        </div>

                                        {/* View Job Posting Link */}
                                        {proposal.metadata?.jobLink && (
                                            <a
                                                href={proposal.metadata.jobLink}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center text-primary-600 hover:text-primary-700 hover:underline text-sm mt-3"
                                            >
                                                View job posting
                                                <ExternalLink className="h-3 w-3 ml-1" />
                                            </a>
                                        )}
                                    </div>

                                    {/* Right sub-column: specs */}
                                    {(proposal.metadata?.experienceLevel || proposal.metadata?.jobBudget || proposal.metadata?.estimatedDuration) && (
                                        <div className="w-48 flex-shrink-0 space-y-4 border-l border-gray-100 pl-6 text-sm">
                                            {proposal.metadata?.experienceLevel && (
                                                <div>
                                                    <div className="text-gray-500 text-xs">Experience level</div>
                                                    <div className="text-gray-900 font-medium capitalize">{proposal.metadata.experienceLevel.toLowerCase()}</div>
                                                </div>
                                            )}
                                            {proposal.metadata?.jobBudget && (
                                                <div>
                                                    <div className="text-gray-500 text-xs">
                                                        {proposal.metadata.jobBudget.type === 'hourly' ? 'Hourly range' : 'Budget'}
                                                    </div>
                                                    <div className="text-gray-900 font-medium">
                                                        {proposal.metadata.jobBudget.type === 'hourly'
                                                            ? `$${proposal.metadata.jobBudget.min || 0} - $${proposal.metadata.jobBudget.max || 0}/hr`
                                                            : `$${proposal.metadata.jobBudget.amount?.toLocaleString() || 0}${proposal.metadata.jobBudget.maxAmount ? ` - $${proposal.metadata.jobBudget.maxAmount.toLocaleString()}` : ''}`
                                                        }
                                                    </div>
                                                </div>
                                            )}
                                            {proposal.metadata?.jobBudget?.engagementType && (
                                                <div>
                                                    <div className="text-gray-500 text-xs">Hours/week</div>
                                                    <div className="text-gray-900 font-medium capitalize">{proposal.metadata.jobBudget.engagementType.replace('_', ' ').toLowerCase()}</div>
                                                </div>
                                            )}
                                            {(proposal.metadata?.estimatedDuration || proposal.metadata?.jobBudget?.duration) && (
                                                <div>
                                                    <div className="text-gray-500 text-xs">Project length</div>
                                                    <div className="text-gray-900 font-medium">{proposal.metadata.estimatedDuration || proposal.metadata.jobBudget?.duration}</div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Skills and Expertise */}
                            {proposal.metadata?.skills && proposal.metadata.skills.length > 0 && (
                                <div className="border border-gray-200 rounded-lg p-5 mb-6">
                                    <h3 className="font-semibold text-gray-900 mb-3">Skills and expertise</h3>
                                    <div className="flex flex-wrap gap-2">
                                        {proposal.metadata.skills.map((skill, index) => (
                                            <span
                                                key={index}
                                                className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                                            >
                                                {skill}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Your Proposed Terms */}
                            <div className="border border-gray-200 rounded-lg p-5 mb-6">
                                <h3 className="font-semibold text-gray-900 mb-4">Your proposed terms</h3>
                                <div className="space-y-4">
                                    <div>
                                        <div className="text-gray-500 text-sm">{proposal.pricing.type === 'hourly' ? 'Hourly rate' : 'Fixed price'}</div>
                                        <div className="text-gray-500 text-xs">Total amount the client will see on your proposal</div>
                                        <div className="text-gray-900 font-medium mt-1">
                                            ${proposal.pricing.amount.toLocaleString()}{proposal.pricing.type === 'hourly' ? '/hr' : ''}
                                        </div>
                                    </div>
                                    {proposal.pricing.type === 'hourly' && (
                                        <div className="pt-3 border-t border-gray-100">
                                            <div className="text-gray-500 text-sm">You'll receive</div>
                                            <div className="text-gray-500 text-xs">The estimated amount you'll receive after service fees</div>
                                            <div className="text-gray-900 font-medium mt-1">
                                                ${(proposal.pricing.amount * 0.9).toFixed(2)}/hr
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Cover Letter */}
                            <div className="border border-gray-200 rounded-lg p-5 mb-6">
                                <h3 className="font-semibold text-gray-900 mb-3">Cover letter</h3>
                                <p className="text-gray-700 text-sm whitespace-pre-wrap">{proposal.coverLetter}</p>
                            </div>

                            {/* Project Plan / Milestones */}
                            {proposal.metadata?.projectPlan?.milestones && proposal.metadata.projectPlan.milestones.length > 0 && (
                                <div className="border border-gray-200 rounded-lg p-5 mb-6">
                                    <h3 className="font-semibold text-gray-900 mb-3">Project Plan & Milestones</h3>
                                    <div className="space-y-3">
                                        {proposal.metadata.projectPlan.milestones.map((milestone, index) => (
                                            <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                                                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-xs font-medium">
                                                    {index + 1}
                                                </div>
                                                <div className="flex-1">
                                                    <p className="text-sm text-gray-700">{milestone.description}</p>
                                                    <div className="flex gap-4 mt-2 text-xs text-gray-500">
                                                        {milestone.amount && (
                                                            <span className="font-medium text-gray-700">
                                                                {milestone.amount} {milestone.currency}
                                                            </span>
                                                        )}
                                                        {milestone.dueDate && (
                                                            <span>
                                                                Due: {new Date(milestone.dueDate).toLocaleDateString()}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Upfront Payment */}
                            {proposal.metadata?.upfrontPaymentPercent !== null && proposal.metadata?.upfrontPaymentPercent > 0 && (
                                <div className="border border-gray-200 rounded-lg p-5 mb-6">
                                    <h3 className="font-semibold text-gray-900 mb-2">Payment Terms</h3>
                                    <p className="text-sm text-gray-700">
                                        Upfront payment: <span className="font-medium">{proposal.metadata.upfrontPaymentPercent}%</span>
                                    </p>
                                </div>
                            )}

                            {/* Annotations / Flags */}
                            {proposal.metadata?.annotations && proposal.metadata.annotations.length > 0 && (
                                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                                    <div className="flex items-start gap-2">
                                        <span className="text-yellow-600">⚠️</span>
                                        <div>
                                            <h3 className="font-medium text-yellow-800 mb-1">Proposal Flags</h3>
                                            <div className="flex flex-wrap gap-2">
                                                {proposal.metadata.annotations.map((annotation, index) => (
                                                    <span 
                                                        key={index}
                                                        className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs font-medium"
                                                    >
                                                        {annotation}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Attachments */}
                            {proposal.attachments && proposal.attachments.length > 0 && (
                                <div className="border border-gray-200 rounded-lg p-5 mb-6">
                                    <h3 className="font-semibold text-gray-900 mb-3">Attachments</h3>
                                    <div className="space-y-2">
                                        {proposal.attachments.map((attachment, index) => (
                                            <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                                <span className="text-2xl">📎</span>
                                                <div className="flex-1">
                                                    <div className="font-medium text-sm">{attachment.name}</div>
                                                    <div className="text-xs text-gray-500">
                                                        {attachment.mimeType || attachment.type} • {(attachment.size / 1024).toFixed(1)} KB
                                                    </div>
                                                </div>
                                                <button
                                                    onClick={() => handleViewAttachment(attachment, index)}
                                                    className="text-primary-600 hover:text-primary-700 text-sm"
                                                >
                                                    View
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Portfolio Items */}
                            {proposal.portfolioItems && proposal.portfolioItems.length > 0 && (
                                <div className="border border-gray-200 rounded-lg p-5 mb-6">
                                    <h3 className="font-semibold text-gray-900 mb-3">Portfolio Items</h3>
                                    <div className="space-y-4">
                                        {proposal.portfolioItems.map((item, index) => (
                                            <div key={index} className="border border-gray-200 rounded-lg p-4">
                                                <div className="flex items-start justify-between">
                                                    <div className="flex-1">
                                                        <div className="font-medium text-gray-900 mb-1">{item.title}</div>
                                                        {item.category && (
                                                            <div className="text-sm text-gray-500 mb-2">{item.category}</div>
                                                        )}
                                                        {item.description && (
                                                            <p className="text-sm text-gray-700 mb-3">{item.description}</p>
                                                        )}

                                                        {/* Technologies */}
                                                        {item.technologies && item.technologies.length > 0 && (
                                                            <div className="mb-3">
                                                                <div className="text-xs font-medium text-gray-600 mb-1">Technologies:</div>
                                                                <div className="flex flex-wrap gap-1">
                                                                    {item.technologies.map((tech, techIndex) => (
                                                                        <span key={techIndex} className="px-2 py-1 bg-primary-100 text-primary-800 rounded-full text-xs">
                                                                            {tech}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Project Details */}
                                                        <div className="grid grid-cols-2 gap-4 mb-3">
                                                            {item.duration && (
                                                                <div>
                                                                    <div className="text-xs font-medium text-gray-600">Duration</div>
                                                                    <div className="text-sm text-gray-700">{item.duration}</div>
                                                                </div>
                                                            )}
                                                            {item.budget && (
                                                                <div>
                                                                    <div className="text-xs font-medium text-gray-600">Budget</div>
                                                                    <div className="text-sm text-gray-700">{item.budget}</div>
                                                                </div>
                                                            )}
                                                        </div>

                                                        {item.client && (
                                                            <div className="mb-3">
                                                                <div className="text-xs font-medium text-gray-600">Client</div>
                                                                <div className="text-sm text-gray-700">{item.client}</div>
                                                            </div>
                                                        )}

                                                        {item.url && (
                                                            <a
                                                                href={item.url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-primary-600 hover:text-primary-700 text-sm flex items-center gap-1"
                                                            >
                                                                <ExternalLink className="h-3 w-3" />
                                                                View Project
                                                            </a>
                                                        )}
                                                    </div>
                                                    {item.imageUrl && (
                                                        <img
                                                            src={item.imageUrl}
                                                            alt={item.title}
                                                            className="h-20 w-20 rounded-lg object-cover ml-4"
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Links */}
                            {proposal.links && proposal.links.length > 0 && (
                                <div className="border border-gray-200 rounded-lg p-5 mb-6">
                                    <h3 className="font-semibold text-gray-900 mb-3">Links</h3>
                                    <div className="space-y-2">
                                        {proposal.links.map((link, index) => (
                                            <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                                <div className="flex-1">
                                                    <div className="font-medium text-sm">{link.title}</div>
                                                    {link.description && (
                                                        <div className="text-xs text-gray-500">{link.description}</div>
                                                    )}
                                                </div>
                                                <a
                                                    href={link.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-primary-600 hover:text-primary-700 text-sm"
                                                >
                                                    {link.url}
                                                </a>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Screening Questions */}
                            {proposal.screeningAnswers && proposal.screeningAnswers.length > 0 && (
                                <div className="border border-gray-200 rounded-lg p-5 mb-6">
                                    <h3 className="font-semibold text-gray-900 mb-3">Screening Questions</h3>
                                    <div className="space-y-4">
                                        {proposal.screeningAnswers.map((qa, index) => (
                                            <div key={index} className="border border-gray-200 rounded-lg p-4">
                                                <div className="font-medium text-gray-900 mb-2">{qa.question}</div>
                                                <div className="text-gray-700">{qa.answer}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Admin Comments */}
                            {proposal.adminComments && (
                                <div className="mb-6">
                                    <h3 className="font-semibold text-gray-900 mb-2">Admin Comments</h3>
                                    <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                                        <p className="text-gray-700">{proposal.adminComments}</p>
                                    </div>
                                </div>
                            )}

                            {proposal.adminFeedback && (
                                <div className="mb-6">
                                    <h3 className="font-semibold text-gray-900 mb-2">Admin Feedback</h3>
                                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                        <p className="text-gray-700">{proposal.adminFeedback}</p>
                                    </div>
                                </div>
                            )}

                            {/* Submission Status - Only for app-created proposals */}
                            {submissionStatus && proposal.source !== 'upwork' && (
                                <div className="border border-gray-200 rounded-lg p-5 mb-6">
                                    <h3 className="font-semibold text-gray-900 mb-3">Submission Status</h3>
                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                        <div><strong>Status:</strong> {submissionStatus.upworkStatus}</div>
                                        <div><strong>Submitted:</strong> {formatDate(submissionStatus.submittedAt)}</div>
                                        <div><strong>Client Viewed:</strong> {submissionStatus.clientViewed ? 'Yes' : 'No'}</div>
                                        <div><strong>Client Interested:</strong> {submissionStatus.clientInterested ? 'Yes' : 'No'}</div>
                                    </div>
                                </div>
                            )}

                            {/* Client Feedback - Only for app-created proposals */}
                            {clientFeedback && proposal.source !== 'upwork' && (
                                <div className="border border-gray-200 rounded-lg p-5 mb-6">
                                    <h3 className="font-semibold text-gray-900 mb-3">Client Feedback</h3>
                                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                        <div className="flex items-center gap-2 mb-2">
                                            <span className="font-medium">Rating:</span>
                                            <span className="text-yellow-500">{'★'.repeat(clientFeedback.feedback.rating)}</span>
                                            <span className="text-sm text-gray-600">({clientFeedback.feedback.rating}/5)</span>
                                        </div>
                                        <p className="text-gray-700 mb-2">{clientFeedback.feedback.comments}</p>
                                        <div className="text-sm text-gray-600">
                                            <strong>Interested:</strong> {clientFeedback.feedback.interested ? 'Yes' : 'No'}
                                        </div>
                                        {clientFeedback.feedback.questions && clientFeedback.feedback.questions.length > 0 && (
                                            <div className="mt-2">
                                                <strong>Client Questions:</strong>
                                                <ul className="list-disc list-inside text-sm text-gray-600 mt-1">
                                                    {clientFeedback.feedback.questions.map((question, index) => (
                                                        <li key={index}>{question}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Sidebar - About the Client */}
                        {proposal.metadata?.clientInfo && (
                            <div className="w-72 flex-shrink-0">
                                <div className="border border-gray-200 rounded-lg p-5 sticky top-6">
                                    <h3 className="font-semibold text-gray-900 mb-4">About the client</h3>
                                    <div className="space-y-3 text-sm">
                                        {proposal.metadata.clientInfo.paymentVerified && (
                                            <div className="flex items-center gap-2 text-success-600 pb-3 border-b border-gray-100">
                                                <CheckCircle className="h-4 w-4" />
                                                Payment method verified
                                            </div>
                                        )}

                                        {proposal.metadata.clientInfo.rating && (
                                            <div className="flex items-center gap-2 pb-3 border-b border-gray-100">
                                                <span className="text-yellow-500">★</span>
                                                <span className="font-medium">{proposal.metadata.clientInfo.rating.toFixed(1)}</span>
                                                {proposal.metadata.clientInfo.totalReviews && (
                                                    <span className="text-xs text-gray-500">({proposal.metadata.clientInfo.totalReviews} reviews)</span>
                                                )}
                                            </div>
                                        )}

                                        {proposal.metadata.clientInfo.country && (
                                            <div className="pb-3 border-b border-gray-100">
                                                <div className="flex items-center gap-2 text-gray-700">
                                                    <MapPin className="h-4 w-4 text-gray-400" />
                                                    <span className="font-medium">{proposal.metadata.clientInfo.country}</span>
                                                </div>
                                                {proposal.metadata.clientInfo.city && (
                                                    <div className="text-gray-500 text-xs ml-6">{proposal.metadata.clientInfo.city}</div>
                                                )}
                                                {proposal.metadata.clientInfo.timezone && (
                                                    <div className="flex items-center gap-2 text-gray-500 text-xs mt-1 ml-6">
                                                        <Clock className="h-3 w-3" />
                                                        {proposal.metadata.clientInfo.timezone}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {proposal.metadata.clientInfo.jobsPosted && (
                                            <div className="pb-3 border-b border-gray-100">
                                                <div className="text-gray-700">{proposal.metadata.clientInfo.jobsPosted} jobs posted</div>
                                            </div>
                                        )}

                                        {proposal.metadata.clientInfo.totalSpent && (
                                            <div className="pb-3 border-b border-gray-100">
                                                <div className="text-gray-700">{proposal.metadata.clientInfo.totalSpent} total spent</div>
                                                {proposal.metadata.clientInfo.totalHires && (
                                                    <div className="text-gray-500 text-xs">{proposal.metadata.clientInfo.totalHires} hires</div>
                                                )}
                                            </div>
                                        )}

                                        {proposal.metadata.clientInfo.memberSince && (
                                            <div className="text-gray-500 text-xs">
                                                Member since {proposal.metadata.clientInfo.memberSince}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* File Viewer Modal */}
            <FileViewerModal
                isOpen={showFileViewer}
                onClose={closeFileViewer}
                proposalId={proposal?._id}
                attachmentIndex={selectedAttachmentIndex}
                attachment={selectedAttachment}
            />
        </div>
    );
}
